import { useEffect, useMemo, useState } from "react";
import logo from "/logo.jpg";
import { login as apiLogin, getUser, getPermissions } from '../api/auth/authApi';
import { handleApiError } from '../api/errorHandling';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermissions } from '../api/auth/PermissionsContext';
import { useAuth } from '../api/auth/authContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Avatar,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Paper,
  Link,
  Divider,
  useTheme,
} from '@mui/material';
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function Login() {
  const [show, setShow] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldError, setFieldError] = useState<{ username?: string; password?: string }>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { clientCode: clientCodeParam } = useParams<{ clientCode: string }>();
  const { setPermissions } = usePermissions();
  const { login } = useAuth();
  const theme = useTheme();

  const isAdminLogin = location.pathname.startsWith("/admin/login");
  const effectiveClientCode = useMemo(() => {
    if (isAdminLogin) return "admin";
    return (clientCodeParam || "").trim();
  }, [clientCodeParam, isAdminLogin]);

  useEffect(() => {
    // Guard: if user hits "/something/login" without a clientCode, redirect to admin login.
    if (!isAdminLogin && !effectiveClientCode) {
      navigate("/admin/login", { replace: true });
    }
  }, [effectiveClientCode, isAdminLogin, navigate]);

  const palette = {
    leftBg: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    leftText: theme.palette.common.white,
    cardBg: theme.palette.background.paper,
    cardShadow: theme.shadows[8],
    fieldBg: theme.palette.background.default,
    border: theme.palette.divider,
    error: theme.palette.error.main,
    button: theme.palette.primary.dark,
    buttonHover: theme.palette.primary.main,
    grayText: theme.palette.text.secondary,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof fieldError = {};
    if (!username) errors.username = "Username is required";
    if (!password) errors.password = "Password is required";
    setFieldError(errors);
    if (Object.keys(errors).length > 0) return;
    setLoading(true);
    try {
      const authResponse = await apiLogin({ username, password, clientCode: effectiveClientCode });
      const accessToken = authResponse.access_token;
      login(accessToken);
      localStorage.setItem("username", username);
      localStorage.setItem("clientCode", effectiveClientCode);
      await getUser(username, effectiveClientCode, accessToken);
      const permissionsResp = await getPermissions(username, effectiveClientCode, accessToken);
      const permissionKeys = permissionsResp.map((perm: any) => perm.functionName);
      setPermissions(permissionKeys);
      localStorage.setItem("permissions", JSON.stringify(permissionKeys));
      navigate("/dashboard");
    } catch (err) {
      handleApiError(err, 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      bgcolor: palette.fieldBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: theme.typography.fontFamily,
    }}>
      <Paper elevation={0} sx={{
        width: { xs: '100%', md: 900 },
        minHeight: { xs: '100vh', md: 540 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        borderRadius: { xs: 0, md: 5 },
        overflow: 'hidden',
        boxShadow: { xs: 'none', md: palette.cardShadow },
        bgcolor: { xs: 'transparent', md: palette.cardBg },
      }}>
        {/* Left Branding Panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: palette.leftBg,
            color: '#222',
            p: { xs: 4, md: 6 },
            minWidth: 0,
            minHeight: { xs: 180, md: 540 },
            position: 'relative',
            width: { xs: '100%', md: '50%' },
          }}
        >
          <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 340, mx: 'auto' }}>
            <Avatar src={logo} alt="ProjectAG Logo" sx={{ width: 90, height: 90, mb: 3, mx: 'auto', boxShadow: 3, bgcolor: '#fff' }} />
            <Typography variant="h3" fontWeight={900} sx={{ mb: 1, letterSpacing: 1, color: theme.palette.primary.dark, fontSize: 36 }}>
              Oresund Integration Platform
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: theme.palette.text.primary, fontSize: 20 }}>
              Enterprise Middleware
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 400, fontSize: 16, mb: 2 }}>
              Connect, automate, and analyze your business operations with a secure, scalable, and modern integration hub.
            </Typography>
          </Box>
        </Box>
        {/* Divider for desktop */}
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, bgcolor: '#e3e8ee', width: 2 }} />
        {/* Login Form Panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: { xs: palette.fieldBg, md: palette.cardBg },
            minHeight: { xs: 360, md: 540 },
            p: { xs: 3, md: 6 },
            width: { xs: '100%', md: '50%' },
          }}
        >
          <Card
            elevation={4}
            sx={{
              width: '100%',
              maxWidth: 370,
              borderRadius: 4,
              p: { xs: 2, md: 4 },
              boxShadow: palette.cardShadow,
              bgcolor: palette.cardBg,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CardContent sx={{ width: '100%', p: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={800} color="#1a237e" gutterBottom sx={{ fontSize: 25, letterSpacing: 0.5 }}>
                  Sign in to OIP
                </Typography>
                <Typography variant="body2" color={palette.grayText} sx={{ mb: 1, fontWeight: 500, fontSize: 15 }}>
                  Enter your credentials to continue
                </Typography>
              </Box>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
                autoComplete="on"
              >
                <TextField
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                  error={!!fieldError.username}
                  helperText={fieldError.username}
                  InputLabelProps={{ sx: { fontWeight: 700, color: theme.palette.primary.dark } }}
                  inputProps={{ 'aria-label': 'username', style: { fontWeight: 600 }, autoComplete: 'username' }}
                  sx={{
                    bgcolor: palette.fieldBg,
                    borderRadius: 2,
                    input: { color: theme.palette.text.primary },
                    // Fix autofill background color
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
                      WebkitTextFillColor: theme.palette.text.primary,
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
                      WebkitTextFillColor: theme.palette.text.primary,
                    },
                    '& input:-webkit-autofill:hover': {
                      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
                      WebkitTextFillColor: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Password"
                  variant="outlined"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  fullWidth
                  error={!!fieldError.password}
                  helperText={fieldError.password}
                  InputLabelProps={{ sx: { fontWeight: 700, color: '#1a237e' } }}
                  inputProps={{ 'aria-label': 'password', style: { fontWeight: 600 }, autoComplete: 'current-password' }}
                  sx={{
                    bgcolor: palette.fieldBg,
                    borderRadius: 2,
                    input: { color: '#1a237e' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #f5f7fa inset',
                      WebkitTextFillColor: '#1a237e',
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #f5f7fa inset',
                      WebkitTextFillColor: '#1a237e',
                    },
                    '& input:-webkit-autofill:hover': {
                      WebkitBoxShadow: '0 0 0 1000px #f5f7fa inset',
                      WebkitTextFillColor: '#1a237e',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={show ? "Hide password" : "Show password"}
                          onClick={() => setShow(s => !s)}
                          edge="end"
                          tabIndex={-1}
                          sx={{ color: '#1a237e' }}
                        >
                          {show ? <FiEyeOff /> : <FiEye />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={e => setRememberMe(e.target.checked)}
                        color="primary"
                        
                        sx={{ color: '#1a237e' }}
                      />
                    }
                    label={<Typography sx={{ fontWeight: 600, color: '#1a237e', fontSize: 15 }}>Remember me</Typography>}
                    sx={{ m: 0 }}
                  />
                  <Link href="#" underline="hover" sx={{ fontWeight: 600, color: '#1a237e', fontSize: 15 }}>
                    Forgot password?
                  </Link>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    fontWeight: 800,
                    borderRadius: 3,
                    py: 1.3,
                    mt: 1,
                    fontSize: 17,
                    bgcolor: palette.button,
                    '&:hover': { bgcolor: palette.buttonHover },
                    boxShadow: '0 2px 8px 0 rgba(26,35,126,0.10)',
                  }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
}